import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Clock, Users2, Loader2, RefreshCw, Lock, Check, Mic, Square, Mail } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../../i18n";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";
import { startCheckout, formatPrice } from "../../lib/checkout";
import { SUBSCRIPTIONS_COMING_SOON } from "../../lib/config";
import { Reveal } from "../motion";

function getClientId() {
  let id = localStorage.getItem("sereva_client_id");
  if (!id) { id = "cid-" + Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem("sereva_client_id", id); }
  return id;
}

const RecipeGenerator = () => {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const clientId = useRef(getClientId());
  const [ingredients, setIngredients] = useState("");
  const [mood, setMood] = useState("");
  const [dietary, setDietary] = useState("");
  const [loading, setLoading] = useState(false);
  const [recipe, setRecipe] = useState(null);
  const [paywall, setPaywall] = useState(false);
  const [plans, setPlans] = useState([]);
  const [quota, setQuota] = useState(null);
  const [subLoading, setSubLoading] = useState(null);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [emailGate, setEmailGate] = useState(false);
  const [email, setEmail] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const moods = t("recipe.moods");
  const unlimited = quota?.unlimited || (user && user !== false && (user.is_member || user.recipe_plan));

  const loadQuota = () => api.get(`/ai/recipe-quota?client_id=${clientId.current}`).then(({ data }) => setQuota(data)).catch(() => {});
  useEffect(() => { loadQuota(); }, [user]); // eslint-disable-line
  useEffect(() => { api.get("/ai/recipe-plans").then(({ data }) => setPlans(data)).catch(() => {}); }, []);

  const generate = async () => {
    setLoading(true);
    setRecipe(null);
    setPaywall(false);
    setEmailGate(false);
    try {
      const { data } = await api.post("/ai/recipe", { ingredients, mood, dietary, lang, client_id: clientId.current });
      setRecipe(data);
      loadQuota();
    } catch (err) {
      if (err.response?.status === 402) {
        setPaywall(true);
      } else {
        toast.error(lang === "fr" ? "Impossible de générer la recette." : "Could not generate the recipe.");
      }
    } finally {
      setLoading(false);
    }
  };

  const submitEmail = async (e) => {
    e?.preventDefault?.();
    if (!email || !email.includes("@")) {
      toast.error(lang === "fr" ? "Entrez un courriel valide." : "Enter a valid email.");
      return;
    }
    setEmailLoading(true);
    try {
      await api.post("/leads/subscribe", { email, client_id: clientId.current, lang, source: "recipe_generator" });
      toast.success(lang === "fr" ? "🌿 3 recettes bonus débloquées !" : "🌿 3 bonus recipes unlocked!");
      setEmailGate(false);
      await loadQuota();
      await generate();
    } catch {
      toast.error(lang === "fr" ? "Un instant, réessayez." : "A moment, try again.");
    } finally {
      setEmailLoading(false);
    }
  };

  const PAYMENT_LINKS = {
    plan_basic: "https://buy.stripe.com/00waEWfoWb8Qcka1AscZa06",
    plan_decouverte: "https://buy.stripe.com/bJeeVcekS2Ck5VM92UcZa02",
  };

  const subscribe = async (lookup_key) => {
    const link = PAYMENT_LINKS[lookup_key];
    if (link) {
      window.location.href = link;
      return;
    }
    if (!user || user === false) { navigate("/login", { state: { from: "/#recipe" } }); return; }
    setSubLoading(lookup_key);
    try {
      await startCheckout({ lookup_key, user_id: user.id });
    } catch {
      toast.error(lang === "fr" ? "Erreur lors de l'abonnement." : "Subscription error.");
      setSubLoading(null);
    }
  };

  const planLabel = (rec) => rec === "week" ? t("recipe.perWeek") : rec === "month" ? t("shop.perMonth") : t("shop.perYear");

  const stopRecording = () => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== "inactive") mr.stop();
  };

  const startRecording = async () => {
    if (transcribing) return;
    if (recording) { stopRecording(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(chunksRef.current, { type: mime || "audio/webm" });
        if (blob.size < 500) {
          toast.error(lang === "fr" ? "Enregistrement trop court." : "Recording too short.");
          return;
        }
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "voice.webm");
          const { data } = await api.post(`/ai/transcribe?lang=${lang}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          const heard = (data.text || "").trim();
          if (!heard) {
            toast.error(lang === "fr" ? "Rien détecté, réessayez." : "Nothing heard, try again.");
          } else {
            setIngredients((prev) => prev ? `${prev}, ${heard}` : heard);
            toast.success(lang === "fr" ? "Ingrédients ajoutés" : "Ingredients added");
          }
        } catch {
          toast.error(lang === "fr" ? "Transcription échouée." : "Transcription failed.");
        } finally {
          setTranscribing(false);
        }
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (err) {
      toast.error(lang === "fr" ? "Micro indisponible. Autorisez l'accès." : "Microphone unavailable. Allow access.");
    }
  };

  return (
    <section id="recipe" data-testid="recipe-generator" className="relative overflow-hidden max-w-[1400px] mx-auto px-6 lg:px-10 py-28 lg:py-36">
      <div className="grid lg:grid-cols-2 gap-px bg-border border border-border rounded-3xl overflow-hidden">
        {/* Left: form */}
        <div className="bg-background p-8 lg:p-14">
          <Reveal>
            <div className="flex items-center gap-2 text-primary/70 text-xs uppercase tracking-[0.25em] mb-6">
              <Sparkles className="w-4 h-4 text-secondary" /> {t("recipe.eyebrow")}
            </div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tightest text-foreground mb-4">{t("recipe.title")}</h2>
            <p className="text-foreground/65 font-light mb-10 max-w-md">{t("recipe.sub")}</p>

            <label className="block text-sm text-foreground/70 mb-2">{t("recipe.ingredients")}</label>
            <div className="relative mb-6">
              <input
                data-testid="recipe-ingredients"
                value={ingredients}
                onChange={(e) => setIngredients(e.target.value)}
                placeholder={t("recipe.ingredientsPh")}
                className="w-full bg-muted pl-4 pr-14 py-3 outline-none focus:ring-1 focus:ring-primary"
              />
              <button
                type="button"
                data-testid="recipe-voice-dictate"
                onClick={startRecording}
                disabled={transcribing}
                title={lang === "fr" ? "Dicter à voix haute les ingrédients" : "Dictate ingredients by voice"}
                aria-label={lang === "fr" ? "Dicter à voix haute" : "Dictate by voice"}
                className={`absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
                  recording ? "bg-red-500 text-white animate-pulse" : transcribing ? "bg-muted-foreground/20 text-foreground/50" : "bg-primary text-primary-foreground hover:bg-primary/90"
                }`}
              >
                {transcribing ? <Loader2 className="w-4 h-4 animate-spin" /> : recording ? <Square className="w-4 h-4" fill="currentColor" /> : <Mic className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-foreground/50 -mt-4 mb-6">{lang === "fr" ? "🎙️ Astuce : cliquez sur le micro pour dicter vos ingrédients à voix haute." : "🎙️ Tip: click the mic to dictate your ingredients."}</p>

            <label className="block text-sm text-foreground/70 mb-2">{t("recipe.mood")}</label>
            <div className="flex flex-wrap gap-2 mb-6">
              {moods.map((m) => (
                <button
                  key={m}
                  data-testid={`mood-${m}`}
                  onClick={() => setMood(m)}
                  className={`px-4 py-2 text-sm border transition-colors duration-200 ${
                    mood === m ? "bg-primary text-primary-foreground border-primary" : "border-border text-foreground/70 hover:border-primary"
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>

            <label className="block text-sm text-foreground/70 mb-2">{t("recipe.dietary")}</label>
            <input
              data-testid="recipe-dietary"
              value={dietary}
              onChange={(e) => setDietary(e.target.value)}
              placeholder="vegan, gluten-free…"
              className="w-full bg-muted px-4 py-3 mb-8 outline-none focus:ring-1 focus:ring-primary"
            />

            <button
              data-testid="recipe-generate"
              onClick={generate}
              disabled={loading}
              className="w-full bg-secondary text-secondary-foreground py-4 text-sm font-medium flex items-center justify-center gap-2 hover:bg-secondary/90 active:scale-[0.98] transition-[transform,background-color] duration-200 disabled:opacity-70"
            >
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> {t("recipe.generating")}</> : <><Sparkles className="w-4 h-4" /> {t("recipe.generate")}</>}
            </button>
            {quota && quota.logged_in && quota.quota > 0 && (
              <p data-testid="recipe-quota" className="text-xs text-center mt-3 text-foreground/50">
                {lang === "fr"
                  ? `${quota.remaining} / ${quota.quota} recettes ce mois`
                  : `${quota.remaining} / ${quota.quota} recipes this month`}
              </p>
            )}
          </Reveal>
        </div>

        {/* Right: result */}
        <div className="bg-muted/40 p-8 lg:p-14 min-h-[420px] flex items-center justify-center relative">
          <AnimatePresence mode="wait">
            {loading ? (
              <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-center text-foreground/50">
                <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
                <p className="font-serif text-xl">{t("recipe.generating")}</p>
              </motion.div>
            ) : emailGate ? (
              <motion.div key="emailgate" data-testid="recipe-email-gate" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="w-full">
                <div className="flex items-center gap-2 text-primary mb-3"><Mail className="w-5 h-5" /><span className="text-xs uppercase tracking-widest">{lang === "fr" ? "Débloquez 3 recettes de plus" : "Unlock 3 more recipes"}</span></div>
                <h3 className="font-serif text-3xl text-foreground mb-2">{lang === "fr" ? "Un courriel, 3 recettes offertes 🌿" : "One email, 3 free recipes 🌿"}</h3>
                <p className="text-sm text-foreground/60 mb-6">{lang === "fr" ? "Entrez votre adresse courriel pour générer 3 recettes supplémentaires. Vous recevrez aussi, de temps à autre, une recette mood-food exclusive." : "Enter your email to generate 3 more recipes. You'll also receive occasional exclusive mood-food recipes."}</p>
                <form onSubmit={submitEmail} className="space-y-3">
                  <input
                    data-testid="recipe-email-input"
                    type="email"
                    required
                    autoFocus
                    placeholder={lang === "fr" ? "votre@courriel.com" : "your@email.com"}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-card border border-border px-4 py-3 outline-none focus:ring-1 focus:ring-primary"
                  />
                  <button
                    data-testid="recipe-email-submit"
                    type="submit"
                    disabled={emailLoading}
                    className="w-full bg-primary text-primary-foreground py-4 text-sm font-medium flex items-center justify-center gap-2 hover:bg-primary/90 active:scale-[0.98] transition disabled:opacity-70"
                  >
                    {emailLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4" /> {lang === "fr" ? "Débloquer mes 3 recettes" : "Unlock my 3 recipes"}</>}
                  </button>
                  <p className="text-xs text-foreground/50 text-center">{lang === "fr" ? "Pas de spam. Désabonnement en 1 clic." : "No spam. 1-click unsubscribe."}</p>
                </form>
              </motion.div>
            ) : paywall ? (
              <motion.div key="paywall" data-testid="recipe-paywall" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="w-full">
                <div className="flex items-center gap-2 text-primary mb-3"><Lock className="w-5 h-5" /><span className="text-xs uppercase tracking-widest">{t("members.locked")}</span></div>
                <h3 className="font-serif text-3xl text-foreground mb-2">{t("recipe.paywallTitle")}</h3>
                <p className="text-sm text-foreground/60 mb-6">{t("recipe.paywallSub")}</p>
                <div className="space-y-3">
                  {plans.map((p) => {
                    const popular = p.lookup_key === "recipe_monthly";
                    return (
                      <div key={p.lookup_key} data-testid={`recipe-plan-${p.lookup_key}`} className={`flex items-center justify-between border p-4 rounded-2xl ${popular ? "border-primary bg-primary/5" : "border-border bg-card"}`}>
                        <div>
                          <p className="font-serif text-lg text-foreground flex items-center gap-2">
                            {p[lang].name.split("—").pop().trim()}
                            {popular && <span className="text-[10px] uppercase tracking-wider bg-secondary text-secondary-foreground px-2 py-0.5 rounded-full">{t("recipe.popular")}</span>}
                          </p>
                          <p className="text-sm text-foreground/60">{formatPrice(p.price, p.currency)}<span className="text-foreground/40">{planLabel(p.recurring)}</span></p>
                        </div>
                        <button
                          data-testid={`recipe-subscribe-${p.lookup_key}`}
                          onClick={() => subscribe(p.lookup_key)}
                          disabled={subLoading === p.lookup_key || SUBSCRIPTIONS_COMING_SOON}
                          className={`text-sm px-5 py-2.5 active:scale-95 transition-[transform,background-color] disabled:opacity-60 flex items-center gap-1.5 ${
                            SUBSCRIPTIONS_COMING_SOON
                              ? "bg-muted text-foreground/60 border border-border cursor-not-allowed"
                              : "bg-primary text-primary-foreground hover:bg-primary/90"
                          }`}
                        >
                          {SUBSCRIPTIONS_COMING_SOON
                            ? (lang === "fr" ? "Bientôt" : "Soon")
                            : subLoading === p.lookup_key ? <Loader2 className="w-4 h-4 animate-spin" /> : (user && user !== false ? t("recipe.subscribe") : <><Check className="w-3.5 h-3.5" />{t("recipe.subscribe")}</>)}
                        </button>
                      </div>
                    );
                  })}
                </div>
                {(!user || user === false) && (
                  <p className="text-xs text-foreground/50 mt-4 text-center">{t("recipe.loginToSub")}</p>
                )}
              </motion.div>
            ) : recipe ? (
              <motion.div
                key="recipe"
                data-testid="recipe-result"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="w-full"
              >
                <h3 className="font-serif text-3xl lg:text-4xl text-foreground leading-tight mb-2">{recipe.title}</h3>
                {recipe.subtitle && <p className="text-foreground/60 italic mb-4">{recipe.subtitle}</p>}
                <div className="flex gap-6 text-sm text-foreground/60 mb-6">
                  {recipe.time && <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" />{recipe.time}</span>}
                  {recipe.servings && <span className="flex items-center gap-1.5"><Users2 className="w-4 h-4" />{recipe.servings}</span>}
                </div>
                {recipe.mood_benefit && (
                  <div className="bg-primary/5 border-l-2 border-secondary px-4 py-3 mb-6">
                    <p className="text-xs uppercase tracking-widest text-primary/70 mb-1">{t("recipe.benefit")}</p>
                    <p className="text-sm text-foreground/80">{recipe.mood_benefit}</p>
                  </div>
                )}
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-primary/70 mb-2">Ingredients</p>
                    <ul className="space-y-1.5 text-sm text-foreground/75">
                      {(recipe.ingredients || []).map((ing, i) => (
                        <li key={i} className="flex gap-2"><span className="text-secondary">·</span>{ing}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-primary/70 mb-2">{t("recipe.steps")}</p>
                    <ol className="space-y-2 text-sm text-foreground/75">
                      {(recipe.steps || []).map((s, i) => (
                        <li key={i} className="flex gap-2"><span className="font-serif text-secondary">{i + 1}.</span>{s}</li>
                      ))}
                    </ol>
                  </div>
                </div>
                {recipe.tip && <p className="mt-6 text-sm text-foreground/60 italic">💡 {t("recipe.tip")}: {recipe.tip}</p>}
                <button onClick={generate} className="mt-6 text-sm text-primary flex items-center gap-1.5 link-underline"><RefreshCw className="w-3.5 h-3.5" /> {t("recipe.generate")}</button>
              </motion.div>
            ) : (
              <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-center text-foreground/40 max-w-xs">
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/sereva/media/77330fd5-ea3b-4a10-b021-17f588293d07.jpeg`} alt="" className="w-full h-48 object-cover mb-6 opacity-90 rounded-2xl" />
                <p className="font-serif text-xl">{lang === "fr" ? "Votre recette apparaîtra ici." : "Your recipe will appear here."}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default RecipeGenerator;
